import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import Papa from 'papaparse';
import {
    addGroup,
    addAdmin,
    addMemberToGroup,
    deleteGroup,
    deleteAdmin,
    deleteMemberFromGroup,
    fetchGroupsByCourseId,
    fetchAdminsByCourseId,
} from '../../../admin';
import GroupList from "./GroupList";
import AdminList from "./AdminList";
import MemberList from "./MemberList";
import BackgroundBlock from "../BackgroundBlock";
import Header from "./Header";
import { HomeHeader } from "../HomeHeader";
import { MdUploadFile } from "react-icons/md";

const GroupManager = () => {
    const location = useLocation();
    const { course } = location.state;
    const fileInputRef = useRef(null);


    const [admins, setAdmins] = useState([]);
    const [newAdminName, setNewAdminName] = useState('');
    const [selectedAdminIndex, setSelectedAdminIndex] = useState(null);
    const [isAdminButtonDisabled, setIsAdminButtonDisabled] = useState(false);


    const [groups, setGroups] = useState([]);
    const [members, setMembers] = useState([]);
    const [newGroupName, setNewGroupName] = useState('');
    const [newMemberName, setNewMemberName] = useState('');
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [selectedGroupIndex, setSelectedGroupIndex] = useState(null);
    const [selectedMemberIndex, setSelectedMemberIndex] = useState(null);
    const [isMemberButtonDisabled, setIsMemberButtonDisabled] = useState(false);
    const [isGroupButtonDisabled, setIsGroupButtonDisabled] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const groupsCount = groups.length;
    const hasGroups = groupsCount > 0;


    useEffect(() => {
        const loadGroups = async () => {
            try {
                const groupsData = await fetchGroupsByCourseId(course.id);
                setGroups(groupsData);
            } catch (error) {
                console.error("Error fetching groups: ", error);
            }
        };

        const loadAdmins = async () => {
            try {
                const adminsData = await fetchAdminsByCourseId(course.id);
                setAdmins(adminsData);
            } catch (error) {
                console.error("Error fetching admins: ", error);
            }
        };

        loadGroups();
        loadAdmins();
    }, [course.id]);

    const handleGroupAction = async () => {
        if (!newGroupName.trim()) {
            alert("Group name is required.");
            return;
        }
        try {
            setIsGroupButtonDisabled(true);
            const newGroupId = await addGroup(course.id, newGroupName || 'Unnamed Group');
            setGroups(prevGroups => [...prevGroups, { id: newGroupId, groupName: newGroupName || 'Unnamed Group', members: [] }]);
            setNewGroupName('');
            setTimeout(() => setIsGroupButtonDisabled(false), 1000);
        } catch (error) {
            console.error("Error adding group: ", error);
            setIsGroupButtonDisabled(false);
        }
    };

    const handleAdminAction = async () => {
        if (!newAdminName.trim()) {
            alert("Admin Email is required.");
            return;
        }
        try {
            setIsAdminButtonDisabled(true);
            console.log(newAdminName);
            await addAdmin(course.id, newAdminName);
            setAdmins(prevAdmins => [...prevAdmins, newAdminName]);
            setNewAdminName('');
            setTimeout(() => setIsAdminButtonDisabled(false), 1000);
        } catch (error) {
            console.error("Error adding group: ", error);
            setIsAdminButtonDisabled(false);
        }
    };

    const deleteSelectedGroup = async () => {
        if (selectedGroupIndex !== null) {
            const groupId = groups[selectedGroupIndex].id;
            try {
                await deleteGroup(course.id, groupId);
                setGroups(prevGroups => prevGroups.filter((_, index) => index !== selectedGroupIndex));
                setSelectedGroupIndex(null);
                setMembers([]);
            } catch (error) {
                console.error("Error deleting group: ", error);
            }
        }
    };

    const deleteSelectedAdmin = async () => {
        if (selectedAdminIndex !== null) {
            const admin = admins[selectedAdminIndex];
            try {
                await deleteAdmin(course.id, admin);
                setAdmins(prevAdmins => prevAdmins.filter((_, index) => index !== selectedAdminIndex));
                setSelectedAdminIndex(null);
            } catch (error) {
                console.error("Error deleting group: ", error);
            }
        }
    };

    const addMember = async () => {
        if (!newMemberName.trim() || !newMemberEmail.trim()) {
            alert("Please provide both member name and email.");
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newMemberEmail)) {
            alert("Please enter a valid email address.");
            return;
        }
        if (selectedGroupIndex === null) {
            alert("Please select a group before adding a member.");
            return;
        }
        const selectedGroup = groups[selectedGroupIndex];
        const memberExists = members.some(member => member.email === newMemberEmail);
        if (memberExists) {
            alert("This member is already in the selected group.");
            return;
        }

        try {
            setIsMemberButtonDisabled(true);
            const response = await addMemberToGroup(course.id, selectedGroup.id, newMemberName, newMemberEmail);

            if (response.success) {
                const newMember = { name: newMemberName, email: newMemberEmail };
                setMembers(prevMembers => [...prevMembers, newMember]);
                setGroups(prevGroups => {
                    const updatedGroups = [...prevGroups];
                    updatedGroups[selectedGroupIndex] = {
                        ...updatedGroups[selectedGroupIndex],
                        members: [...updatedGroups[selectedGroupIndex].members, newMember]
                    };
                    return updatedGroups;
                });
                setNewMemberName('');
                setNewMemberEmail('');
            } else {
                alert(response.message);
            }
        } catch (error) {
            console.error('Error adding member:', error);
            alert('An error occurred while adding the member. Please try again.');
        } finally {
            setTimeout(() => setIsMemberButtonDisabled(false), 1000);
        }
    };

    const addMemberByConfig = async (memberName, memberEmail, selectedGroupId) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(memberEmail)) {
            alert("Please enter a valid email address.");
            return;
        }

        const memberExists = members.some(member => member.email === memberEmail);
        if (memberExists) {
            alert("This member is already in the selected group.");
            return;
        }

        try {
            setIsMemberButtonDisabled(true);
            const response = await addMemberToGroup(course.id, selectedGroupId, memberName, memberEmail);

            if (!response.success) {
                alert(response.message);
            }
        } catch (error) {
            console.error('Error adding member:', error);
            alert('An error occurred while adding the member. Please try again.');
        } finally {
            setTimeout(() => setIsMemberButtonDisabled(false), 1000);
        }
    };

    const deleteSelectedMember = async () => {
        if (selectedMemberIndex !== null && selectedGroupIndex !== null) {
            const selectedMember = members[selectedMemberIndex];
            const groupId = groups[selectedGroupIndex].id;

            try {
                const result = await deleteMemberFromGroup(course.id, groupId, selectedMember.email);
                if (result.success) {
                    setMembers(prevMembers => prevMembers.filter((_, index) => index !== selectedMemberIndex));
                    setGroups(prevGroups => {
                        const updatedGroups = [...prevGroups];
                        updatedGroups[selectedGroupIndex] = {
                            ...updatedGroups[selectedGroupIndex],
                            members: updatedGroups[selectedGroupIndex].members.filter(member => member.email !== selectedMember.email)
                        };
                        return updatedGroups;
                    });
                    setSelectedMemberIndex(null);
                } else {
                    alert(result.message);
                }
            } catch (error) {
                console.error('Error deleting member:', error);
                alert('An error occurred while deleting the member. Please try again.');
            }
        }
    };

    const handleGroupSelect = (index) => {
        setSelectedGroupIndex(selectedGroupIndex === index ? null : index);
        setSelectedMemberIndex(null);
        if (selectedGroupIndex !== index) {
            setMembers(groups[index]?.members || []);
        } else {
            setMembers([]);
        }
    };

    const handleAdminSelect = (index) => {
        setSelectedAdminIndex(selectedAdminIndex === index ? null : index);
    };

    const handleMemberSelect = (index) => {
        setSelectedMemberIndex(selectedMemberIndex === index ? null : index);
    };

    const handleConfigFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'text/csv') {
            alert('Please upload CSV file');
            return;
        }

        setIsUploading(true);

        try {
            Papa.parse(file, {
                complete: async (results) => {
                    const rows = results.data;

                    const requiredColumns = ['name', 'login_id', 'group_name'];
                    const missingColumns = requiredColumns.filter(column => !(column in rows[0]));
                    if (missingColumns.length > 0) {
                        alert(`Missing required column: ${missingColumns.join(', ')}`);
                        setIsUploading(false);
                        return;
                    }

                    const groups = {};
                    for (let i = 0; i < rows.length; i++) {
                        const row = rows[i];

                        let name = row['name'];
                        if (name.includes(',')) {
                            const parts = name.split(',').map(part => part.trim());
                            name = `${parts[1]} ${parts[0]}`;
                        }


                        const loginId = `${row['login_id']}@uci.edu`;
                        const groupName = row['group_name'];

                        if (!groups[groupName]) {
                            groups[groupName] = [];
                        }

                        groups[groupName].push({ name, login_id: loginId });
                    }

                    for (const [groupName, members] of Object.entries(groups)) {
                        const newGroupId = await addGroup(course.id, groupName || 'Unnamed Group');
                        setGroups(prevGroups => [...prevGroups, { id: newGroupId, groupName: groupName || 'Unnamed Group', members: [] }]);

                        for (const member of members) {
                            await addMemberByConfig(member.name, member.login_id, newGroupId);
                        }
                    }

                    setNewGroupName('');
                    const groupsData = await fetchGroupsByCourseId(course.id);
                    setGroups(groupsData);
                    alert('File handling success !');
                },
                header: true,
                skipEmptyLines: true
            });
        } catch (error) {
            console.error('Error handling file:', error);
            alert('Error handling file, try again');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleUploadButtonClick = () => {
        fileInputRef.current?.click();
    };


    return (
        <>
            <HomeHeader text="Group Manager" link="/admin/home" showPrivacyButton />
            <PageWrapper>
                <BackgroundBlock>
                    <Header 
                        course={course}
                        groupsCount={groupsCount}
                        hasGroups={hasGroups}
                    />
                    <Card>
                        <CardContainerRow>
                            <GroupList
                                groups={groups}
                                selectedGroupIndex={selectedGroupIndex}
                                handleGroupSelect={handleGroupSelect}
                                handleGroupAction={handleGroupAction}
                                deleteSelectedGroup={deleteSelectedGroup}
                                isButtonDisabled={isGroupButtonDisabled}
                                newGroupName={newGroupName}
                                setNewGroupName={setNewGroupName}
                            />

                            <MemberList
                                members={members}
                                selectedMemberIndex={selectedMemberIndex}
                                handleMemberSelect={handleMemberSelect}
                                addMember={addMember}
                                deleteSelectedMember={deleteSelectedMember}
                                newMemberName={newMemberName}
                                setNewMemberName={setNewMemberName}
                                newMemberEmail={newMemberEmail}
                                isButtonDisabled={isMemberButtonDisabled}
                                setNewMemberEmail={setNewMemberEmail}
                                selectedGroupIndex={selectedGroupIndex}
                            />

                            <AdminList
                                admins={admins}
                                selectedAdminIndex={selectedAdminIndex}
                                handleAdminSelect={handleAdminSelect}
                                handleAdminAction={handleAdminAction}
                                deleteSelectedAdmin={deleteSelectedAdmin}
                                isButtonDisabled={isAdminButtonDisabled}
                                newAdminName={newAdminName}
                                setNewAdminName={setNewAdminName}
                            />
                        </CardContainerRow>
                        <BottomSection>
                            <HiddenInput
                                type="file"
                                accept=".csv"
                                ref={fileInputRef}
                                onChange={handleConfigFileUpload}
                            />
                            <UploadButton
                                onClick={handleUploadButtonClick}
                                disabled={isUploading}
                            >
                                <ButtonContent>
                                    <MdUploadFile size={20} />
                                    {isUploading ? "Processing..." : "Upload Group Data"}
                                </ButtonContent>
                            </UploadButton>
                        </BottomSection>
                    </Card>
                </BackgroundBlock>
            </PageWrapper>
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f8fafc;
    display: flex;
    flex-direction: column;
    position: relative;
    min-height: 100vh;
`;

const CardContainerRow = styled.div`
    display: grid;
    justify-content: space-between;
    width: 100%;
    gap: 1rem;
    @media (min-width: 768px) {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 2fr) minmax(0, 1.2fr);
    }
    justify-items: stretch; /* default, but explicit */
    align-items: start;
`;

const BottomSection = styled.div`

`;

const HiddenInput = styled.input`
    display: none;
`;

const UploadButton = styled.button`
    display: inline-flex;  
    background-color: #5c5b5b;
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 20px;
    transition: background-color 0.3s;
    background: #374151; color: #fff;

    &:hover {
        background-color: #9333ea;
    }

    &:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
    }
`;

const Card = styled.section`
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 6px 18px rgba(0,0,0,.06);
  padding: 1rem; /* p-6 */
  width: 100%;
  margin: 0 20px;
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  gap: 0.5rem;   /* space between icon and text */
  
  svg {
    color: inherit;  /* make the icon inherit button text color */
    vertical-align: middle;
    margin-top: -4px;
  }
`;

export default GroupManager;
